import { Api } from 'Utils/api';
import type {
  ProjectStageConfig,
  ProjectStatus,
  Pipeline,
  CustomField,
  CompanyOption,
  FieldMapping,
  SourceField,
} from './types';

const getHeaders = (ssoToken: string) => ({
  'X-GHL-SSO': ssoToken,
});

// --- Error helper ---

export const parseApiError = (err: unknown): string => {
  const axiosErr = err as {
    response?: { data?: { message?: string; errors?: Record<string, string[]> } };
  };
  const errors = axiosErr?.response?.data?.errors;
  if (errors) {
    return Object.values(errors).flat()[0] || 'Validation failed';
  }
  return axiosErr?.response?.data?.message || 'An unexpected error occurred';
};

// --- SSO-scoped endpoints (no locationId param) ---

export const listProjectStageConfigs = async (
  ssoToken: string
): Promise<ProjectStageConfig[]> => {
  const response = await Api.get('/ghl/embed/project-stage-configs', {
    headers: getHeaders(ssoToken),
  });
  return response.data?.data || response.data || [];
};

export const createProjectStageConfig = async (
  payload: {
    pipeline_id: string;
    trigger_stage_id: string;
    project_status_id: number;
    company_id?: number | null;
    is_active?: boolean;
    sync_contact_notes?: boolean;
    field_mappings?: Omit<FieldMapping, 'id'>[];
  },
  ssoToken: string
): Promise<ProjectStageConfig> => {
  const response = await Api.post('/ghl/embed/project-stage-configs', payload, {
    headers: getHeaders(ssoToken),
  });
  return response.data?.data || response.data;
};

export const updateProjectStageConfig = async (
  id: number,
  payload: {
    pipeline_id: string;
    trigger_stage_id: string;
    project_status_id: number;
    company_id?: number | null;
    is_active: boolean;
    sync_contact_notes?: boolean;
    field_mappings: Omit<FieldMapping, 'id'>[];
  },
  ssoToken: string
): Promise<ProjectStageConfig> => {
  const response = await Api.put(
    `/ghl/embed/project-stage-configs/${id}`,
    payload,
    { headers: getHeaders(ssoToken) }
  );
  return response.data?.data || response.data;
};

export const toggleProjectStageConfigActive = async (
  id: number,
  isActive: boolean,
  ssoToken: string
): Promise<ProjectStageConfig> => {
  const response = await Api.patch(
    `/ghl/embed/project-stage-configs/${id}/active`,
    { is_active: isActive },
    { headers: getHeaders(ssoToken) }
  );
  return response.data?.data || response.data;
};

export const deleteProjectStageConfig = async (
  id: number,
  ssoToken: string
): Promise<void> => {
  await Api.delete(`/ghl/embed/project-stage-configs/${id}`, {
    headers: getHeaders(ssoToken),
  });
};

export const getCompanies = async (
  ssoToken: string
): Promise<CompanyOption[]> => {
  const response = await Api.get('/ghl/embed/project-stage-configs/companies', {
    headers: getHeaders(ssoToken),
  });
  return response.data?.data || response.data || [];
};

export const getProjectStatuses = async (
  ssoToken: string
): Promise<ProjectStatus[]> => {
  const response = await Api.get('/ghl/embed/project-statuses', {
    headers: getHeaders(ssoToken),
  });
  return response.data?.data || response.data || [];
};

// --- Existing endpoints (locationId as query param) ---

export const getPipelines = async (
  locationId: string,
  ssoToken: string
): Promise<Pipeline[]> => {
  const response = await Api.get('/ghl/embed/pipelines', {
    params: { locationId },
    headers: getHeaders(ssoToken),
  });

  const raw = response.data?.pipelines || response.data?.data || response.data || [];
  const pipelines = Array.isArray(raw) ? raw : [];
  return pipelines.map(
    (p: { id: string; name: string; stages?: { id: string; name: string; sort_order?: number }[] }) => ({
      id: p.id,
      name: p.name,
      stages: (p.stages || [])
        .map((s) => ({ id: s.id, name: s.name, sort_order: s.sort_order }))
        .sort((a: { sort_order?: number }, b: { sort_order?: number }) =>
          (a.sort_order ?? 0) - (b.sort_order ?? 0)
        ),
    })
  );
};

export const getCustomFields = async (
  locationId: string,
  ssoToken: string
): Promise<CustomField[]> => {
  const response = await Api.get('/ghl/embed/custom-fields', {
    params: { locationId },
    headers: getHeaders(ssoToken),
  });

  const raw = response.data?.customFields || response.data?.data || response.data || [];
  const fields = Array.isArray(raw) ? raw : [];

  return fields.map((f: { id: string; name: string; fieldKey?: string; field_key?: string; objectType?: string; object_type?: string; model?: string }) => ({
    id: f.id,
    name: f.name,
    field_key: f.fieldKey || f.field_key || '',
    object_type: f.objectType || f.object_type || f.model || '',
  }));
};

export const getSourceFields = async (
  ssoToken: string
): Promise<SourceField[]> => {
  const response = await Api.get('/ghl/embed/project-stage-configs/source-fields', {
    headers: getHeaders(ssoToken),
  });
  return response.data?.data || response.data || [];
};
